import classNames from "classnames/bind";
import React from "react";
import { toast as sonnerToast, type ToastToDismiss } from "sonner";
import { Button } from "../Button";
import { Icon, IconLoading } from "../Icon";
import styles from "./Toast.module.scss";
import {
  type ToastConfigProps,
  type ToastInStackProps,
  type ToastProps,
  type ToastPushAsyncReturnType,
} from "./types";

const cx = classNames.bind(styles);

/**
 * A small informational message surfaced at the highest layer of an app,
 * usually after some user action
 */
const Toast = <TContext extends Record<string, any> = Record<string, any>>({
  id,
  intent = "note",
  title,
  description,
  slotRight,
  slotLeft,
  isDismissible = true,
  onCancel,
  cancelTriggerProps,
  isBusy,
  classNames,
  styles,
  colorScheme,
}: ToastProps<TContext>) => {
  const getDefaultLeftSlot = (intent: ToastProps["intent"], isBusy = false) => {
    if (isBusy) {
      return (
        <IconLoading
          speed="fast"
          size={24}
        />
      );
    }

    switch (intent) {
      case "success":
        return (
          <Icon
            variant="check-circle"
            size={24}
          />
        );
      case "info":
        return (
          <Icon
            variant="info"
            size={24}
          />
        );
      case "warning":
        return (
          <Icon
            variant="warning"
            size={24}
          />
        );
      case "error":
        return (
          <Icon
            variant="warning-circle"
            size={24}
          />
        );
      default:
        return null;
    }
  };

  const isSlotLeftShown = intent !== "note" || slotLeft || isBusy;

  return (
    <div
      className={cx(
        "toast",
        classNames?.container,
        colorScheme && `color-scheme-${colorScheme}`
      )}
      role="alert"
      style={styles?.container ?? {}}
    >
      {isSlotLeftShown ? (
        <div
          className={cx(
            "toast-slot-left",
            {
              [intent]: !isBusy,
            },
            classNames?.slotLeft
          )}
          style={styles?.slotLeft ?? {}}
        >
          {slotLeft?.({ iconProps: { size: 24 } }) ??
            getDefaultLeftSlot(intent, isBusy)}
        </div>
      ) : null}
      <div
        className={cx(
          "toast-content",
          {
            [intent]: !isBusy,
            "next-to-left-slot": isSlotLeftShown,
          },
          classNames?.content
        )}
        style={styles?.content ?? {}}
      >
        <div className={cx("toast-content-wrapper")}>
          <p className={cx("title")}>{title}</p>
          <p className={cx("desc")}>{description}</p>
        </div>
        {slotRight || isDismissible ? (
          <div
            className={cx(
              "toast-slot-right",
              {
                [intent]: !isBusy,
              },
              classNames?.slotRight
            )}
            style={styles?.slotRight ?? {}}
          >
            {slotRight?.({
              iconProps: { size: 20 },
              buttonProps: { size: "xx-small", variant: "outline" },
              isDismissible,
              cancel: () => {
                onCancel?.(toast.get(id));
              },
              dismiss: () => {
                sonnerToast.dismiss(id);
              },
            }) ??
              (isDismissible ? (
                <Button
                  variant="ghost"
                  icon="x-circle"
                  title="Cancel"
                  iconProps={{
                    alt: "Cancel Icon",
                  }}
                  className={cx("cancel-button")}
                  {...cancelTriggerProps}
                  onClick={() => {
                    sonnerToast.dismiss(id);
                  }}
                />
              ) : null)}
          </div>
        ) : null}
      </div>
    </div>
  );
};

/**
 * utilities to interact with the toast stack
 */
export const toast = {
  /**
   * push a new toast onto the toast stack
   * @param toast properties of the toast. can be a string or a set of
   * properties.
   * @param config configuration of the toast
   * @returns the unique ID of the toast
   */
  push: <TContext extends Record<string, any> = Record<string, any>>(
    toastProps:
      | string
      | Omit<ToastProps, "id" | "colorScheme" | "isDismissible">,
    config?: ToastConfigProps<TContext>
  ): string | number => {
    const actualConfig = {
      ...(config ?? {}),
      dismissible:
        config?.isDismissible !== undefined ? config.isDismissible : true,
    };

    if (typeof toastProps === "string") {
      return sonnerToast.custom(
        (id) => (
          <Toast
            id={id}
            intent="note"
            description={toastProps}
            isDismissible={actualConfig.dismissible}
          />
        ),
        actualConfig
      );
    }

    return sonnerToast.custom(
      (id) => (
        <Toast<TContext>
          id={id}
          {...toastProps}
          isDismissible={actualConfig.dismissible}
          {...(config?.invert !== undefined
            ? { colorScheme: config.invert ? "dark" : "light" }
            : {})}
        />
      ),
      actualConfig
    );
  },
  /**
   * push a new toast onto the toast stack and wait for the provided promise to
   * settle
   * @param promise promise to wait for settling
   * @param props the first toast to render
   * @param config the config for the first toast to render. recommend setting
   * duration to Infinity so the toast doesn't dissapear while waiting for the
   * promise to settle.
   * @returns if the promise resolves, you'll get the promise return as "data"
   * and the first rendered toasts' details, so you can update it if you wish.
   */
  pushAsync: <
    TContext extends Record<string, any> = Record<string, any>,
    TPromiseReturn = any,
  >(
    promise: Promise<TPromiseReturn> | (() => Promise<TPromiseReturn>),
    props: string | Omit<ToastProps, "id" | "colorScheme" | "isDismissible">,
    config?: ToastConfigProps<TContext>
  ) => {
    const p = typeof promise === "function" ? promise() : promise;

    return new Promise<ToastPushAsyncReturnType<TContext, TPromiseReturn>>(
      (res, rej) => {
        let cancelled = false;
        const firstToastId = toast.push<TContext>(
          typeof props === "string"
            ? props
            : {
                ...props,
                onCancel: (t) => {
                  props.onCancel?.(t);
                  cancelled = true;
                  rej(new Error("Cancelled toast.pushAsync() promise early"));
                },
              },
          config
        );

        p.then((data) => {
          if (cancelled) return;

          const firstToastInHistory = (
            sonnerToast.getHistory() as Array<
              ToastInStackProps<TContext> | ToastToDismiss
            >
          ).find((t) => t.id === firstToastId) ?? { id: firstToastId };
          const firstToastInStack = (
            sonnerToast.getToasts() as Array<
              ToastInStackProps<TContext> | ToastToDismiss
            >
          ).find((t) => t.id === firstToastId);

          res({
            data,
            toastId: firstToastId,
            toast: firstToastInHistory,
            isHistory: !firstToastInStack && !!firstToastInHistory,
          });
        }).catch((err) => {
          rej({
            error: err,
            toastId: firstToastId,
            toast: firstToastId,
          });
        });
      }
    );
  },
  /** pull a toast off the toast stack. Provide an ID to remove a specific
   * toast, or just call without one to remove the entire stack. Note, this will
   * not trigger the onDismiss callback provided in the push() method */
  pull: sonnerToast.dismiss,
  /** get a single toast currently in the toast stack */
  get: <TContext extends Record<string, any> = Record<string, any>>(
    id: ToastProps["id"]
  ): ToastInStackProps<TContext> | ToastToDismiss | undefined =>
    sonnerToast.getToasts().find((t) => t.id === id),
  /** get the details of all toasts currently in the toast stack */
  getAll: <TContext extends Record<string, any> = Record<string, any>>(): Array<
    ToastInStackProps<TContext> | ToastToDismiss
  > => sonnerToast.getToasts(),
  /** get the details of all toasts ever pushed */
  getHistory: <
    TContext extends Record<string, any> = Record<string, any>,
  >(): Array<ToastInStackProps<TContext> | ToastToDismiss> =>
    sonnerToast.getHistory(),
};

export default Toast;
